// src/lib/jobInvoiceConvert.ts
//
// Shared helper for the job→invoice convert flow. Two consumers — the JobDrawer's
// handleGenerateInvoice in FieldHub.tsx and the equivalent on the JobDetail page.
// Both call sites need to project normalized `field_job_line_items` rows into the
// `invoices.line_items` JSONB array shape, so the projection lives in one place
// to keep them aligned.
//
// The DB row shape (FieldJobLineItem) has DB-only fields (workspace_id,
// field_job_id, sort_index, timestamps) that don't belong in the invoice JSONB.
// The InvoiceLineItem shape matches the existing invoice JSONB convention seen
// in src/pages/payments/InvoiceDetail.tsx — id is a stringified timestamp,
// name/description are first-class, qty/unit_price/total are numerics.
//
// Also exports fetchContactEmail — used by all three job→invoice creation
// sites (FieldHub.handleGenerateInvoice, JobDetail.handleGenerateInvoice,
// BillingTab consolidated convert) so the invoice carries the contact's
// email forward instead of dropping it (the original bug: Send-invoice and
// receipt-email were both silently no-op'd when the convert paths
// hardcoded contact_email='').
// ============================================================================

import { supabase } from './supabase'
import type { PricingType, PricingTier } from './totals'

export type FieldJobLineItem = {
  id: string
  workspace_id: string
  field_job_id: string
  name: string
  description: string | null
  qty: number
  unit: string | null
  unit_price: number
  // Internal cost basis (what the line costs us), staff-only. Nullable: null =
  // not entered / margin unknown. NEVER projected into the invoice JSONB — see
  // the warning on projectFieldJobLineItemsToInvoiceJsonb below.
  unit_cost: number | null
  total: number
  product_id: string | null
  sort_index: number
  created_at: string
  updated_at: string
}

export type InvoiceLineItem = {
  id: string
  name: string
  description: string
  qty: number
  unit: string
  unit_price: number
  total: number
  product_id?: string
  // Arc B Slice 2 — per-line pricing rides in the invoice JSONB. Job-sourced
  // lines are always 'flat' (field_job_line_items has no tier columns yet).
  pricing_type?: PricingType | null
  tiers?: PricingTier[] | null
  // Arc B Slice 3 — per-line modifiers (fee + multiplier). Job lines have none
  // (the projector omits them), but the type carries them for invoice-side lines.
  line_fee?: number | null
  price_multiplier?: number | null
}

// Pure shape-builder for the consolidated invoice INSERT row literal.
// Sibling to projectFieldJobLineItemsToInvoiceJsonb — kept here so the
// per-job and consolidated convert paths share one source of truth for
// what an invoices row looks like, and any column drift fails-loud at
// the type-system layer.
//
// The IO orchestration (gathering line items, INSERT, the load-bearing
// UPDATE with .in('id', ids).is('invoice_id', null)) stays at the call
// site so the race-guard predicate is visible to reviewers. This helper
// only builds the literal.
//
// Q1 (same-contact validation) MUST happen at the call site before this
// helper is invoked — the helper trusts that all selected rows share
// firstSelectedRow.contact_id, snapshots that contact's identity, and
// produces one invoice with one billing contact.
//
// Q2 issue_date = today (consolidation date), Q3 tax_rate = 0 (operator
// sets on InvoiceDetail), Q4 contact snapshot from the first row — all
// per the Chunk C strategist decisions.
export interface BuildConsolidatedInvoiceInsertArgs {
  workspaceId: string
  /** contact_email is required — fetched via fetchContactEmail() at the
   *  call site (the field_jobs row doesn't carry it). Empty string is the
   *  honest default when the contact has no email on file. */
  firstSelectedRow: { contact_id: string; contact_name: string; contact_email: string; company_name: string | null }
  mergedLineItems: InvoiceLineItem[]
  discountType: 'none' | 'percentage' | 'fixed'
  discountValue: number
  totals: { subtotal: number; discountAmount: number; taxAmount: number; total: number }
  issueDate: string  // 'yyyy-MM-dd'
  currency?: string
}

// invoice_number is intentionally NOT set — the assign_invoice_number
// BEFORE-INSERT trigger (Phase A migration 20260615000000) atomically
// claims the next per-workspace number from workspace_invoice_counters.
export function buildConsolidatedInvoiceInsert(args: BuildConsolidatedInvoiceInsertArgs) {
  return {
    workspace_id:   args.workspaceId,
    contact_id:     args.firstSelectedRow.contact_id,
    contact_name:   args.firstSelectedRow.contact_name,
    contact_email:  args.firstSelectedRow.contact_email,   // carried forward from contacts.email
    company_name:   args.firstSelectedRow.company_name || null,
    status:         'draft',
    line_items:     args.mergedLineItems,
    subtotal:       args.totals.subtotal,
    tax_rate:       0,                                     // Q3 — operator sets on InvoiceDetail
    tax_amount:     args.totals.taxAmount,
    total:          args.totals.total,
    amount_due:     args.totals.total,
    amount_paid:    0,
    discount_type:  args.discountType,
    discount_value: args.discountValue,
    discount_amount: args.totals.discountAmount,
    currency:       args.currency ?? 'USD',
    issue_date:     args.issueDate,                        // Q2 — today, not any visit's date
    notes:          '',                                    // operator adds on InvoiceDetail
  }
}

/**
 * Looks up a contact's email by id. Returns '' when contactId is nullish,
 * the contact doesn't exist, or the contact has no email on file —
 * empty-string is the honest default that downstream consumers (Send
 * invoice, receipt-email checkbox) already gate against. Wrapping every
 * job→invoice creation site through this helper means there is ONE
 * canonical lookup; sites can't drift on what "fetch the contact's
 * email" looks like.
 *
 * Uses .maybeSingle() — a contact whose id doesn't resolve (deleted
 * mid-flow, dangling FK from a broken seed) yields data === null
 * rather than throwing, and we return '' so the invoice INSERT still
 * proceeds. The contact_id FK does its own integrity check.
 */
export async function fetchContactEmail(contactId: string | null): Promise<string> {
  if (!contactId) return ''
  const { data } = await supabase
    .from('contacts')
    .select('email')
    .eq('id', contactId)
    .maybeSingle()
  return (data?.email || '').trim()
}

// Fresh IDs are stringified-timestamp form (e.g. "1777504786550") matching the
// pattern InvoiceDetail's addLineItem uses. Adding the row index to the base
// timestamp guarantees uniqueness within a single call (Date.now() resolution
// is millisecond, but multiple rows in one tick would otherwise collide).
export function projectFieldJobLineItemsToInvoiceJsonb(
  rows: FieldJobLineItem[],
): InvoiceLineItem[] {
  const base = Date.now()
  return rows.map((row, i) => {
    // ⚠️ CONFIDENTIALITY — DO NOT add row.unit_cost (or any cost/margin field)
    // to this object. invoices.line_items is shipped to the customer's browser
    // WHOLESALE via the portal_invoices view + PortalInvoices.tsx (raw jsonb
    // passthrough), so any key here leaks to the customer. Cost is staff-only;
    // it stays on field_job_line_items and is never snapshotted into the
    // invoice jsonb. Enumerate explicit keys only — never spread `...row`.
    const item: InvoiceLineItem = {
      id: String(base + i),
      name: row.name,
      description: row.description ?? '',
      qty: Number(row.qty) || 0,
      unit: row.unit ?? 'unit',
      unit_price: Number(row.unit_price) || 0,
      total: Number(row.total) || 0,
      // Slice 2: job lines have no tier columns → explicitly 'flat'. (No tiers
      // key — the stored total already equals qty × unit_price for these.)
      // Slice 3: no line_fee / price_multiplier emitted either (absent = none) —
      // job lines carry no modifiers.
      pricing_type: 'flat',
    }
    if (row.product_id) item.product_id = row.product_id
    return item
  })
}
